import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DayEndClosure } from '@/app/types/dayEndClosure';
import dayEndClosureService from '@/app/services/dayEndClosureService';
import { format } from 'date-fns';
import { IndianRupee, Eye, CheckCircle, XCircle, Clock, Calendar, Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ClosureDetailDialog } from './ClosureDetailDialog';
import { CloseDayDialog } from './CloseDayDialog';

interface ClosureHistoryTableProps {
  closures: DayEndClosure[];
  onRefresh?: () => void;
}

export const ClosureHistoryTable: React.FC<ClosureHistoryTableProps> = ({
  closures,
  onRefresh
}) => {
  const [selectedClosure, setSelectedClosure] = useState<DayEndClosure | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [closeDayDialogOpen, setCloseDayDialogOpen] = useState(false);
  const [closureToClose, setClosureToClose] = useState<DayEndClosure | null>(null);

  const handleViewDetails = async (closure: DayEndClosure) => {
    // Try to fetch full details with denominations
    try {
      const fullClosure = await dayEndClosureService.getClosureById(closure.id);
      setSelectedClosure(fullClosure);
    } catch (error) {
      // If fetching full details fails, use what we have
      setSelectedClosure(closure);
    }
    setDetailDialogOpen(true);
  };

  const handleCloseDay = (closure: DayEndClosure) => {
    setClosureToClose(closure);
    setCloseDayDialogOpen(true);
  };

  const handleCloseDaySuccess = () => {
    setCloseDayDialogOpen(false);
    setClosureToClose(null);
    if (onRefresh) {
      onRefresh();
    }
  };
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_started': { variant: 'secondary' as const, label: 'Not Started', icon: Clock },
      'open': { variant: 'default' as const, label: 'Open', icon: Clock },
      'closing': { variant: 'outline' as const, label: 'Closing', icon: Clock },
      'closed': { variant: 'success' as const, label: 'Closed', icon: CheckCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount: string | undefined) => {
    if (!amount) return '—';
    const value = parseFloat(amount);
    return (
      <span className="flex items-center gap-1">
        <IndianRupee className="w-3 h-3" />
        {value.toLocaleString('en-IN')}
      </span>
    );
  };

  const getDiscrepancyDisplay = (discrepancy: string | undefined) => {
    if (!discrepancy) return '—';
    const value = parseFloat(discrepancy);
    if (value === 0) {
      return (
        <span className="text-green-600 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Balanced
        </span>
      );
    }
    return (
      <span className={`flex items-center gap-1 ${value < 0 ? 'text-red-600' : 'text-amber-600'}`}>
        {value < 0 && <XCircle className="w-3 h-3" />}
        <IndianRupee className="w-3 h-3" />
        {Math.abs(value).toLocaleString('en-IN')}
        {value < 0 ? ' Short' : ' Over'}
      </span>
    );
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Closure History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Opening Balance</TableHead>
                <TableHead>Closing Balance</TableHead>
                <TableHead>Opening Discrepancy</TableHead>
                <TableHead>Closing Discrepancy</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {closures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No closure history found
                  </TableCell>
                </TableRow>
              ) : (
                closures.map((closure) => (
                  <TableRow key={closure.id}>
                    <TableCell className="font-medium">
                      {format(new Date(closure.business_date), 'dd MMM yyyy')}
                    </TableCell>
                    <TableCell>{getStatusBadge(closure.status)}</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="cursor-help">
                              {formatCurrency(closure.opening_balance_physical || closure.opening_balance_software)}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1 text-xs">
                              <div>Software: {formatCurrency(closure.opening_balance_software)}</div>
                              <div>Physical: {formatCurrency(closure.opening_balance_physical)}</div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="cursor-help">
                              {formatCurrency(closure.closing_balance_physical || closure.closing_balance_software)}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1 text-xs">
                              <div>Software: {formatCurrency(closure.closing_balance_software)}</div>
                              <div>Physical: {formatCurrency(closure.closing_balance_physical)}</div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>{getDiscrepancyDisplay(closure.opening_discrepancy)}</TableCell>
                    <TableCell>{getDiscrepancyDisplay(closure.closing_discrepancy)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(closure)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {closure.status === 'open' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCloseDay(closure)}
                          >
                            <Lock className="w-4 h-4 mr-1" />
                            Close Day
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>

      {/* Detail Dialog */}
      <ClosureDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        closure={selectedClosure}
      />

      {/* Close Day Dialog */}
      {closureToClose && (
        <CloseDayDialog
          open={closeDayDialogOpen}
          onOpenChange={setCloseDayDialogOpen}
          closure={closureToClose}
          onSuccess={handleCloseDaySuccess}
        />
      )}
    </>
  );
};